import { motion } from 'framer-motion'
import { Activity, CheckCircle, AlertTriangle, XCircle, Clock, Globe, Wifi, RefreshCw } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocale } from '../i18n/Link'
import SEO, { schemas } from '../components/SEO'

const services = [
  { key: 'web', status: 'operational', uptime: '99.99%' },
  { key: 'api', status: 'operational', uptime: '99.99%' },
  { key: 'relay', status: 'operational', uptime: '99.95%' },
  { key: 'fileTransfer', status: 'operational', uptime: '99.99%' },
  { key: 'auth', status: 'operational', uptime: '100%' },
  { key: 'billing', status: 'operational', uptime: '99.99%' },
]

const regions = [
  { key: 'euFrankfurt', status: 'operational', latency: '12ms' },
  { key: 'usEast', status: 'operational', latency: '45ms' },
  { key: 'usWest', status: 'operational', latency: '78ms' },
  { key: 'asiaSingapore', status: 'operational', latency: '142ms' },
  { key: 'asiaTokyo', status: 'operational', latency: '156ms' },
]

const incidents = [
  { key: 'apiLatency', status: 'resolved' },
  { key: 'maintenance', status: 'resolved' },
]

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'operational': return <CheckCircle className="w-5 h-5 text-success" />
    case 'degraded': return <AlertTriangle className="w-5 h-5 text-accent" />
    case 'outage': return <XCircle className="w-5 h-5 text-danger" />
    default: return <Clock className="w-5 h-5 text-gray-500" />
  }
}

export default function Status() {
  const { t } = useTranslation(['status', 'common'])
  const { htmlLang } = useLocale()
  const [lastUpdated, setLastUpdated] = useState(new Date())
  useEffect(() => { const interval = setInterval(() => setLastUpdated(new Date()), 60000); return () => clearInterval(interval) }, [])
  const allOperational = services.every(s => s.status === 'operational')

  const getStatusText = (status: string) => {
    switch (status) {
      case 'operational': return t('statusText.operational')
      case 'degraded': return t('statusText.degraded')
      case 'outage': return t('statusText.outage')
      default: return t('statusText.unknown')
    }
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}  className="pt-32 pb-24">
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/status"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.status'), url: '/status' }])}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-success/10 border border-success/20 rounded-full mb-4">
            <Activity className="w-4 h-4 text-success" /><span className="text-success font-medium">{t('allOperational')}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">{t('title')}</h1>
          <p className="text-gray-400">{t('lastUpdated', { time: lastUpdated.toLocaleTimeString(htmlLang) })}</p>
        </motion.div>

        {/* Overall Status */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className={`glass rounded-2xl p-8 mb-8 ${allOperational ? 'border border-success/30' : ''}`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className={`w-4 h-4 rounded-full ${allOperational ? 'bg-success animate-pulse' : 'bg-accent'}`} />
              <div>
                <h2 className="text-xl font-bold text-white">{allOperational ? t('allOperational') : t('someIssues')}</h2>
                <p className="text-gray-400 text-sm">{allOperational ? t('noIncidents') : t('issuesText')}</p>
              </div>
            </div>
            <RefreshCw className="w-5 h-5 text-gray-500 cursor-pointer hover:text-white transition-colors" />
          </div>
          <div className="space-y-3">
            {services.map((service) => (
              <div key={service.key} className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                <div className="flex items-center gap-3">{getStatusIcon(service.status)}<span className="text-white">{t(`services.${service.key}`)}</span></div>
                <div className="flex items-center gap-4">
                  <span className="text-gray-500 text-sm">{t('uptime', { value: service.uptime })}</span>
                  <span className={`text-sm ${service.status === 'operational' ? 'text-success' : service.status === 'degraded' ? 'text-accent' : 'text-danger'}`}>{getStatusText(service.status)}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Regions */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass rounded-2xl p-6 mb-8">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Globe className="w-5 h-5 text-primary" /> {t('regionsTitle')}</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {regions.map((region) => (
              <div key={region.key} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2"><Wifi className="w-4 h-4 text-gray-500" /><span className="text-gray-300 text-sm">{t(`regions.${region.key}`)}</span></div>
                <span className="text-success text-sm">{region.latency}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Incidents */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass rounded-2xl p-6 mb-8">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-primary" /> {t('incidentsTitle')}</h3>
          <div className="space-y-4">
            {incidents.map((incident) => (
              <div key={incident.key} className="p-4 bg-white/5 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-medium">{t(`incidents.${incident.key}.title`)}</h4>
                  <span className="px-2 py-1 bg-success/20 text-success text-xs rounded">{incident.status === 'resolved' ? t('resolved') : t('ongoing')}</span>
                </div>
                <p className="text-gray-400 text-sm mb-2">{t(`incidents.${incident.key}.description`)}</p>
                <div className="flex items-center gap-4 text-xs text-gray-500"><span>{t(`incidents.${incident.key}.date`)}</span><span>{t('duration', { duration: t(`incidents.${incident.key}.duration`) })}</span></div>
              </div>
            ))}
          </div>
        </motion.div>

      </div>
    </motion.div>
  )
}
